/**
 * SharedRateGenerator - Service für konsistente Rate-Generierung in Monte-Carlo-Simulationen
 *
 * Dieser kritische Service gewährleistet faire Vergleiche zwischen verschiedenen Portfolio-Strategien
 * durch die Verwendung identischer Marktszenarien für alle Simulationen. Dies eliminiert
 * Zufallseffekte und ermöglicht valide statistische Vergleiche.
 *
 * Kernfunktionen:
 * - Generierung konsistenter Rate-Sequenzen für 1-Topf vs. 3-Topf Vergleiche
 * - Einheitliche Marktszenarien für alle Asset-Klassen (Inflation, Tagesgeld, Aktien, Anleihen)
 * - Deterministische Reproduzierbarkeit von Simulationsergebnissen
 * - Optimierte Performance durch Batch-Generierung von Rate-Arrays
 * - Integration mit RateCalculationService für verschiedene Berechnungsstrategien
 *
 * Essenziell für statistisch valide Monte-Carlo-Analysen mit bis zu 10.000 Iterationen.
 * Ohne diesen Service wären Strategie-Vergleiche durch unterschiedliche Zufallsszenarien verfälscht.
 *
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 * @since 1.5.0
 */

import { SteuerParameter } from '@/types';
// RateCalculationService will be loaded dynamically
import { ThreeTopfSimulationEngine } from './ThreeTopfSimulationEngine';
import { GARCHSequenceService } from './GARCHSequenceService';
import { GARCHUtils } from '../utils/garchModeling';

/**
 * Konfigurationsoptionen für Rate-Generierung in Simulationen
 *
 * Definiert die Strategien für alle Asset-Klassen und wirtschaftlichen Indikatoren,
 * die für konsistente Monte-Carlo-Simulationen verwendet werden.
 */
export interface SimulationRateOptions {
  /** Inflationsstrategie: 'keine-inflation' | 'parameter-inflation' | 'historische-inflation' | 'statistisch-inflation' */
  inflationOption: string;

  /** Investitionsrendite-Strategie: 'keine-rendite' | 'konstante-rendite' | 'historische-rendite' | 'statistisch-rendite' */
  investmentReturnOption: string;

  /** Tagesgeld-Strategie: 'keine-rendite' | 'historisch-tagesgeld' | 'statistisch-tagesgeld' */
  tagesgeldOption: string;

  /** Zinstopf-Strategie (nur für 3-Topf): 'keine-rendite' | 'historisch-zinstopf' | 'statistisch-zinstopf' */
  zinstopfOption?: string;
}

/**
 * Vollständiger Satz konsistenter Raten für eine Monte-Carlo-Simulation
 *
 * Diese Datenstruktur enthält alle benötigten Raten für eine komplette Simulationsiteration
 * und gewährleistet, dass beide Strategien (1-Topf und 3-Topf) identische Marktbedingungen erleben.
 */
export interface SharedRates {
  /** Jährliche Inflationsraten für die gesamte Simulationsdauer */
  inflationRates: number[];

  /** Jährliche Zinssätze für Verbrauchstopf (Tagesgeld-ähnliche Anlagen) */
  verbrauchstopfRates: number[];

  /** Jährliche Renditen für Investitionstopf (Aktien-ETF-ähnliche Anlagen) */
  investitionstopfRates: number[];

  /** Jährliche Zinssätze für Zinstopf (Staatsanleihen/Festgeld, nur für 3-Topf) */
  zinstopfRates: number[];

  /** Initiale Kapitalaufteilung zwischen den Töpfen (für konsistente Startbedingungen) */
  initialValues?: {
    /** Startkapital Verbrauchstopf in Euro */
    verbrauchstopf: number;

    /** Startkapital Zinstopf in Euro */
    zinstopf: number;

    /** Startkapital Investitionstopf in Euro */
    investitionstopf: number;
  };
}

/**
 * SharedRateGenerator - Hauptklasse für konsistente Rate-Generierung
 *
 * Diese Klasse verwaltet die Generierung konsistenter Marktszenarien für faire
 * Monte-Carlo-Vergleiche zwischen verschiedenen Portfolio-Strategien.
 */
export class SharedRateGenerator {
  /** Steuerliche und finanzielle Grundparameter der Simulation */
  private steuerParameter: SteuerParameter;

  /** Konfigurierte Rate-Berechnungsstrategien für alle Asset-Klassen */
  private options: SimulationRateOptions;

  /** Static cache for consistent individual simulations */
  private static staticRateCache = new Map<string, SharedRates>();

  /**
   * Erstellt einen neuen SharedRateGenerator mit spezifischen Parametern
   *
   * @param steuerParameter - Steuerliche Grundparameter (Zinssätze, Renditen, etc.)
   * @param options - Rate-Berechnungsstrategien für alle Asset-Klassen
   *
   * @example
   * ```typescript
   * const generator = new SharedRateGenerator(steuerParameter, {
   *   inflationOption: 'historische-inflation',
   *   investmentReturnOption: 'statistisch-rendite',
   *   tagesgeldOption: 'parameter-rendite',
   *   zinstopfOption: 'historisch-zinstopf'
   * });
   * ```
   */
  constructor(steuerParameter: SteuerParameter, options: SimulationRateOptions) {
    this.steuerParameter = steuerParameter;
    this.options = options;
  }


  /**
   * Generiert konsistente Raten für eine einzelne Monte-Carlo-Iteration
   *
   * Diese Methode wird vom CombinedMonteCarloEngine verwendet um sicherzustellen,
   * dass 1-Topf und 3-Topf Strategien in derselben Iteration identische Marktbedingungen erleben.
   *
   * @param entnahmeJahre - Anzahl der Jahre mit Kapitalentnahmen
   * @returns Vollständiger SharedRates-Satz für eine konsistente Simulation
   *
   * @example
   * ```typescript
   * const generator = new SharedRateGenerator(steuerParams, options);
   * const rates = generator.generateRatesForIteration(30); // 30 Jahre Entnahmephase
   *
   * console.log(`${rates.inflationRates.length} Inflationsraten generiert`);
   * console.log(`Startkapital: ${rates.initialValues?.verbrauchstopf} € Verbrauchstopf`);
   *
   * // Verwendung in beiden Strategien für fairen Vergleich
   * const result1Topf = simulate1Topf(rates);
   * const result3Topf = simulate3Topf(rates);
   * ```
   */
  async generateRatesForIteration(
    entnahmeJahre: number,
    iteration: number = 0
  ): Promise<SharedRates> {
    const sharedRates: SharedRates = {
      inflationRates: [],
      verbrauchstopfRates: [],
      investitionstopfRates: [],
      zinstopfRates: [],
    };

    // Check if we're using GARCH options
    let isUsingGARCH = GARCHUtils.isGARCHOption(this.options.investmentReturnOption);

    // Common rate options object
    const rateOptions = {
      inflationOption: this.options.inflationOption,
      investmentReturnOption: this.options.investmentReturnOption,
      tagesgeldOption: this.options.tagesgeldOption,
      zinstopfOption: this.options.zinstopfOption,
    };

    // For GARCH: Generate the entire investment rate sequence upfront
    if (isUsingGARCH) {
      const seed = GARCHSequenceService.generateConsistentSeed(
        this.steuerParameter,
        rateOptions,
        iteration
      );

      // Use centralized GARCH service
      // +1: Die Engines simulieren Jahre 0..entnahmeJahre INKLUSIVE (entnahmeJahre + 1 Einträge)
      const garchRates = await GARCHSequenceService.generateGARCHSequence(
        rateOptions,
        entnahmeJahre + 1,
        seed,
        'static',
        iteration,
        this.steuerParameter
      );

      if (garchRates) {
        sharedRates.investitionstopfRates = garchRates;
      } else {
        // Fall back to standard year-by-year generation
        isUsingGARCH = false;
      }
    }

    // Load RateCalculationService for synchronous use
    const { RateCalculationService } = await import('./RateCalculationService');

    // Generate rates for all years upfront
    // WICHTIG: <= statt <, da die Engines Jahre 0..entnahmeJahre INKLUSIVE simulieren
    // (entnahmeJahre + 1 Einträge). Mit < bekäme das letzte Jahr keine Rate (Index-Zugriff
    // würde per "|| 0" auf 0% Rendite/Zins/Inflation maskiert).
    for (let year = 0; year <= entnahmeJahre; year++) {
      // Inflation rates (same for GARCH and non-GARCH) - now with year parameter for deterministic historical selection
      sharedRates.inflationRates.push(
        RateCalculationService.calculateInflationRate(
          rateOptions,
          this.steuerParameter,
          year,
          iteration
        )
      );

      // Verbrauchstopf rates (same for GARCH and non-GARCH) - now with year parameter
      sharedRates.verbrauchstopfRates.push(
        RateCalculationService.calculateVerbrauchstopfRate(
          rateOptions,
          this.steuerParameter,
          year,
          iteration
        )
      );

      // Investitionstopf rates: only generate year-by-year if NOT using GARCH - now with year parameter
      if (!isUsingGARCH) {
        sharedRates.investitionstopfRates.push(
          RateCalculationService.calculateInvestitionstopfRate(
            rateOptions,
            this.steuerParameter,
            year,
            iteration
          )
        );
      }

      // Zinstopf rates (only for 3-Topf, same for GARCH and non-GARCH) - now with year parameter
      if (this.options.zinstopfOption) {
        sharedRates.zinstopfRates.push(
          RateCalculationService.calculateZinstopfRate(
            rateOptions,
            this.steuerParameter,
            year,
            iteration
          )
        );
      }
    }

    // Calculate initial values using 3-Topf engine (both strategies use same initial capital)
    try {
      const engine = new ThreeTopfSimulationEngine(this.steuerParameter, {
        ...this.options,
        zinstopfOption: this.options.zinstopfOption || 'parameter-rendite',
      });
      const initialValues = engine.getInitialValues();

      sharedRates.initialValues = {
        verbrauchstopf: initialValues.verbrauchstopf,
        zinstopf: initialValues.zinstopf,
        investitionstopf: initialValues.investitionstopf,
      };
    } catch (error) {
      console.error('Failed to calculate initial values:', error);
      // Provide fallback values if engine creation fails
      sharedRates.initialValues = {
        verbrauchstopf: 0,
        zinstopf: 0,
        investitionstopf: this.steuerParameter.vorhandenesKapital,
      };
    }

    return sharedRates;
  }

  /**
   * Generiert konsistente Raten für die angegebene Anzahl von Jahren (statische Methode)
   *
   * Diese statische Methode bietet eine direkte API für die Rate-Generierung ohne
   * Instanziierung der Klasse und wird für Batch-Berechnungen verwendet.
   *
   * @param maxYears - Maximale Anzahl Jahre für die Rate-Generierung
   * @param steuerParameter - Steuerliche Grundparameter
   * @param options - Rate-Berechnungsstrategien
   * @returns Vollständiger SharedRates-Satz für konsistente Simulationen
   *
   * @example
   * ```typescript
   * const maxYears = SharedRateGenerator.calculateMaxYears(steuerParameter);
   * const sharedRates = SharedRateGenerator.generateSharedRates(
   *   maxYears,
   *   steuerParameter,
   *   options
   * );
   *
   * // Verwendung für multiple Simulationen mit identischen Marktbedingungen
   * const scenarios = [sharedRates];
   * const results = scenarios.map(rates => runSimulation(rates));
   * ```
   */
  static async generateSharedRates(
    maxYears: number,
    steuerParameter: SteuerParameter,
    options: SimulationRateOptions
  ): Promise<SharedRates> {
    // Create cache key for consistent rates across individual simulations
    // CACHE-KEY-FIX: Die Basis-Parameter renditeInvestitionstopf/inflationsrate/
    // zinssatzVerbrauchstopf/zinssatzZinstopf ergaenzt. Auf ihnen beruhen die
    // 'konstante-rendite'/'parameter-inflation'/'parameter-rendite'-Strategien direkt;
    // fehlten sie im Key, lieferte der statische Cache bei geaenderten Parametern (ohne
    // vorheriges clearStaticCache()) veraltete Raten. Korrektheit haengt damit nicht mehr
    // allein an der Clearing-Disziplin. (Entnahmerate bleibt bewusst draussen: die
    // generierten Markt-/Inflationsraten haengen nicht von ihr ab -> Fair-Comparison.)
    const cacheKey = `${maxYears}-${steuerParameter.vorhandenesKapital}-${steuerParameter.retirementAge}-${steuerParameter.berechnungBisLebensalter}-${steuerParameter.renditeInvestitionstopf}-${steuerParameter.inflationsrate}-${steuerParameter.zinssatzVerbrauchstopf}-${steuerParameter.zinssatzZinstopf}-${options.inflationOption}-${options.investmentReturnOption}-${options.tagesgeldOption}-${options.zinstopfOption}`;

    // Check cache first for consistent individual simulations
    const cachedRates = SharedRateGenerator.staticRateCache.get(cacheKey);
    if (cachedRates) {
      return cachedRates;
    }

    const sharedRates: SharedRates = {
      inflationRates: [],
      verbrauchstopfRates: [],
      investitionstopfRates: [],
      zinstopfRates: [],
    };

    // Check if we're using GARCH options
    let isUsingGARCH = GARCHSequenceService.isUsingGARCH(options);

    // Common rate options object
    const rateOptions = {
      inflationOption: options.inflationOption,
      investmentReturnOption: options.investmentReturnOption,
      tagesgeldOption: options.tagesgeldOption,
      zinstopfOption: options.zinstopfOption,
    };

    // For GARCH: Generate the entire investment rate sequence upfront
    if (isUsingGARCH) {
      const seed = GARCHSequenceService.generateConsistentSeed(steuerParameter, rateOptions, 0); // Use iteration 0 for static method

      // Use centralized GARCH service
      const garchRates = await GARCHSequenceService.generateGARCHSequence(
        rateOptions,
        maxYears,
        seed,
        'static',
        0
      );

      if (garchRates) {
        sharedRates.investitionstopfRates = garchRates;
      } else {
        // Fall back to standard year-by-year generation
        isUsingGARCH = false;
      }
    }

    // Load RateCalculationService for synchronous use
    const { RateCalculationService } = await import('./RateCalculationService');

    // Generate rates for all years upfront
    for (let year = 0; year < maxYears; year++) {
      // Inflation rates (same for GARCH and non-GARCH) - with year parameter for deterministic historical selection
      sharedRates.inflationRates.push(
        RateCalculationService.calculateInflationRate(rateOptions, steuerParameter, year, 0)
      );

      // Verbrauchstopf rates (same for GARCH and non-GARCH) - with year parameter
      sharedRates.verbrauchstopfRates.push(
        RateCalculationService.calculateVerbrauchstopfRate(rateOptions, steuerParameter, year, 0)
      );

      // Investitionstopf rates: only generate year-by-year if NOT using GARCH - with year parameter
      if (!isUsingGARCH) {
        sharedRates.investitionstopfRates.push(
          RateCalculationService.calculateInvestitionstopfRate(
            rateOptions,
            steuerParameter,
            year,
            0
          )
        );
      }

      // Zinstopf rates (only for 3-Topf, same for GARCH and non-GARCH) - with year parameter
      if (options.zinstopfOption) {
        sharedRates.zinstopfRates.push(
          RateCalculationService.calculateZinstopfRate(rateOptions, steuerParameter, year, 0)
        );
      }
    }

    // Cache the generated rates for consistent individual simulations
    SharedRateGenerator.staticRateCache.set(cacheKey, sharedRates);

    return sharedRates;
  }

  /**
   * Clears the static cache for fresh rate generation
   * Used when user explicitly requests fresh randomization
   */
  static clearStaticCache(): void {
    SharedRateGenerator.staticRateCache.clear();
  }

  /**
   * Berechnet die maximale Anzahl Jahre, die für eine Simulation benötigt wird
   *
   * Diese Hilfsmethode bestimmt die Simulationsdauer basierend auf den Lebensalter-Parametern
   * und ist entscheidend für die korrekte Dimensionierung der Rate-Arrays.
   *
   * @param steuerParameter - Steuerparameter mit Lebensalter-Informationen
   * @returns Maximale Anzahl Simulationsjahre (Entnahmephase + 1 Pufferjahr)
   *
   * @example
   * ```typescript
   * const steuerParameter = {
   *   retirementAge: 67,           // Renteneintritt mit 67
   *   berechnungBisLebensalter: 95 // Berechnung bis 95 Jahre
   * };
   *
   * const maxYears = SharedRateGenerator.calculateMaxYears(steuerParameter);
   * console.log(`Simulationsdauer: ${maxYears} Jahre`); // "29 Jahre" (95-67+1)
   *
   * // Verwendung für Array-Dimensionierung
   * const rateArrays = {
   *   inflation: new Array(maxYears),
   *   investment: new Array(maxYears)
   * };
   * ```
   */
  static calculateMaxYears(steuerParameter: SteuerParameter): number {
    return (
      Math.max(0, steuerParameter.berechnungBisLebensalter - steuerParameter.retirementAge) + 1
    );
  }
}
